import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Brain, AlertCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { loadFHIRData } from '@/utils/fhirDataUtils';
import { HealthScore } from '@/types/healthScore';

interface AISummaryData {
  overall: string;
  insights: string[];
  recommendations: string[];
}

interface AISummaryProps {
  selectedPatientId: string;
  onScoreCalculated: (score: HealthScore) => void;
  onSummaryGenerated: (summary: AISummaryData | null) => void;
}

export const AISummary = ({ selectedPatientId, onScoreCalculated, onSummaryGenerated }: AISummaryProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const getRetryKey = (patientId: string) => `ai_summary_retry_${patientId}`;
  const getSummaryKey = (patientId: string) => `ai_summary_${patientId}`;
  const getFhirHashKey = (patientId: string) => `fhir_hash_${patientId}`;
  const getScoreKey = (patientId: string) => `health_score_${patientId}`;

  const generateFhirHash = (fhirData: string): string => {
    // Simple hash function for FHIR data
    let hash = 0;
    for (let i = 0; i < fhirData.length; i++) {
      const char = fhirData.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString();
  };

  const shouldRetry = (patientId: string) => {
    const retryTime = localStorage.getItem(getRetryKey(patientId));
    if (!retryTime) return true;
    
    const retryTimestamp = parseInt(retryTime);
    const now = Date.now();
    const threeMinutes = 3 * 60 * 1000;
    
    return now - retryTimestamp > threeMinutes;
  };

  const setRetryDelay = (patientId: string) => {
    localStorage.setItem(getRetryKey(patientId), Date.now().toString());
  };

  const isCacheValid = (patientId: string, currentFhirData: string): boolean => {
    const cachedSummary = localStorage.getItem(getSummaryKey(patientId));
    const cachedFhirHash = localStorage.getItem(getFhirHashKey(patientId));
    
    if (!cachedSummary || !cachedFhirHash) return false;
    
    const currentFhirHash = generateFhirHash(currentFhirData);
    return cachedFhirHash === currentFhirHash;
  };

  const fetchAISummary = async (patientId: string, attempt: number = 0) => {
    if (attempt === 0) {
      setIsLoading(true);
      setError(null);
    }

    try {
      // Get FHIR data from IndexedDB
      const bundle = await loadFHIRData(patientId);
      
      if (!bundle) {
        setError('No medical data available for AI analysis');
        setIsLoading(false);
        onSummaryGenerated(null);
        return;
      }

      const bundleJson = JSON.stringify(bundle);
      console.log(`Fetching AI summary for patient: ${patientId}, attempt: ${attempt + 1}`);
      
      const token = localStorage.getItem('bearer_token');
      const response = await fetch('/api/summary', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: bundleJson,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const summaryData = await response.json();
      onSummaryGenerated(summaryData.summary);
      onScoreCalculated(summaryData.score);
      setError(null);
      setRetryCount(0);
      
      // Cache the summary, score, and FHIR data hash
      const fhirHash = generateFhirHash(bundleJson);
      localStorage.setItem(getSummaryKey(patientId), JSON.stringify(summaryData.summary));
      localStorage.setItem(getScoreKey(patientId), JSON.stringify(summaryData.score));
      localStorage.setItem(getFhirHashKey(patientId), fhirHash);
      
      // Clear retry delay
      localStorage.removeItem(getRetryKey(patientId));
      
    } catch (err) {
      console.error('AI Summary fetch error:', err);
      onSummaryGenerated(null);
      
      if (attempt < 2) {
        // Retry up to 2 more times
        console.log(`Retrying AI summary fetch, attempt ${attempt + 2}`);
        setRetryCount(attempt + 1);
        setTimeout(() => fetchAISummary(patientId, attempt + 1), 1000);
      } else {
        // All retries failed
        setError('AI health summary failed to load but will be tried again in 3 minutes');
        setRetryDelay(patientId);
        setRetryCount(0);
      }
    } finally {
      if (attempt === 0) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    const loadSummary = async () => {
      // Get FHIR data from IndexedDB
      const bundle = await loadFHIRData(selectedPatientId);
      
      if (!bundle) {
        setError('No medical data available for AI analysis');
        onSummaryGenerated(null);
        return;
      }

      const bundleJson = JSON.stringify(bundle);
      
      // Check if cached summary is valid (FHIR data hasn't changed)
      if (isCacheValid(selectedPatientId, bundleJson)) {
        const cachedSummary = localStorage.getItem(getSummaryKey(selectedPatientId));
        const cachedScore = localStorage.getItem(getScoreKey(selectedPatientId));
        if (cachedSummary && cachedScore) {
          console.log('Using cached AI summary and score for patient:', selectedPatientId);
          onSummaryGenerated(JSON.parse(cachedSummary));
          onScoreCalculated(JSON.parse(cachedScore));
          setError(null); // Clear any previous errors
          return;
        }
      }

      // FHIR data has changed or no cache exists, invalidate cache
      console.log('FHIR data changed or no cache, invalidating AI summary cache for patient:', selectedPatientId);
      localStorage.removeItem(getSummaryKey(selectedPatientId));
      localStorage.removeItem(getFhirHashKey(selectedPatientId));
      localStorage.removeItem(getScoreKey(selectedPatientId));

      // Check if we should retry or wait
      if (shouldRetry(selectedPatientId)) {
        fetchAISummary(selectedPatientId);
      } else {
        setError('AI health summary failed to load but will be tried again in 3 minutes');
        onSummaryGenerated(null);
      }
    };

    loadSummary();
  }, [selectedPatientId]);

  // This is now a "headless" component. It renders nothing.
  return null;
};
